import { io, type Socket } from 'socket.io-client';

import { getApiHost } from '@/shared/lib';

import type { AiAgentSendMessagePayload, AiSocketEventMap } from './types';

/** Обработчик события сокета в реестре подписок; конкретные типы данных проверяются в on()/off() */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SocketListener = (...args: any[]) => void;

/**
 * Singleton-класс соединения Socket.IO с бэкендом AI-агента.
 * Соединение живёт, пока его удерживает хотя бы один потребитель (счётчик ссылок).
 */
export class AiSocket {
  private static instance: AiSocket | null = null;
  private socket: Socket | null = null;
  private refCount = 0;
  /** Незавершённое подключение: параллельные вызовы connect() ждут его, а не поднимают второй сокет */
  private pendingConnection: { promise: Promise<Socket>; abort: (reason: Error) => void } | null = null;
  /**
   * Подписки потребителей: имя события → обработчики.
   * Хранятся в классе, а не только на инстансе сокета: сокет пересоздаётся, а подписчики о его смене не знают.
   */
  private listeners = new Map<string, Set<SocketListener>>();

  private constructor() {}

  static getInstance(): AiSocket {
    if (!AiSocket.instance) {
      AiSocket.instance = new AiSocket();
    }
    return AiSocket.instance;
  }

  /**
   * Захватывает ссылку на соединение, поднимая сокет, если его ещё нет.
   * Ссылка засчитывается только после успешного подключения: при ошибке вызывающему нечего освобождать,
   * иначе счётчик разъезжается с disconnect() и сокет остаётся висеть навсегда.
   */
  async connect(): Promise<Socket> {
    const socket = this.socket ?? this.createSocket();
    const connectedSocket = socket.connected ? socket : await this.openConnection(socket);

    this.refCount++;
    return connectedSocket;
  }

  /** Освобождает ссылку на соединение. Когда ссылок не остаётся — сокет закрывается. */
  disconnect(): void {
    this.refCount--;
    if (this.refCount <= 0) {
      this.closeSocket();
    }
  }

  /**
   * Отправляет сообщение в чат с AI-агентом по уже открытому соединению.
   * Ссылку не захватывает: освободить её было бы некому и счётчик рос бы с каждым сообщением.
   * Возвращает признак отправки: без открытого соединения сообщение никуда не уходит и ответа на него не будет —
   * вызывающему нужно знать об этом, иначе он будет ждать ответ вечно.
   */
  sendMessage(payload: AiAgentSendMessagePayload): boolean {
    if (!this.socket?.connected) {
      return false;
    }

    this.socket.emit('ai_agent:send_message', payload);
    return true;
  }

  /** Подписка на типизированное событие сокета. Тип callback определяется автоматически по имени события. */
  on<E extends keyof AiSocketEventMap>(event: E, callback: (data: AiSocketEventMap[E]) => void): void {
    this.addListener(event, callback as SocketListener);
  }

  /** Отписка от типизированного события сокета. */
  off<E extends keyof AiSocketEventMap>(event: E, callback: (data: AiSocketEventMap[E]) => void): void {
    this.removeListener(event, callback as SocketListener);
  }

  /**
   * Подписка на разрыв соединения.
   * Нужна тем, кто ждёт ответа сервера: после разрыва ответ уже не придёт и ожидание надо прекращать.
   */
  onDisconnect(callback: () => void): void {
    this.addListener('disconnect', callback);
  }

  /** Отписка от разрыва соединения. */
  offDisconnect(callback: () => void): void {
    this.removeListener('disconnect', callback);
  }

  get connected(): boolean {
    return !!this.socket?.active;
  }

  /** Запоминает подписку и вешает её на текущий сокет, если он уже создан. */
  private addListener(event: string, callback: SocketListener): void {
    const callbacks = this.listeners.get(event) ?? new Set<SocketListener>();

    callbacks.add(callback);
    this.listeners.set(event, callbacks);
    this.socket?.on(event, callback);
  }

  /** Снимает подписку и с реестра, и с текущего сокета. */
  private removeListener(event: string, callback: SocketListener): void {
    this.listeners.get(event)?.delete(callback);
    this.socket?.off(event, callback);
  }

  /** Создаёт сокет без подключения — подключением управляет connect(). */
  private createSocket(): Socket {
    const socket = io(getApiHost(), {
      path: '/socket.io',
      withCredentials: true,
      transports: ['websocket'],
      autoConnect: false,
    });

    // Ранее сделанные подписки переносим на новый инстанс — иначе они остались бы на закрытом сокете
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => socket.on(event, callback));
    });

    this.socket = socket;
    return socket;
  }

  /** Ожидание установки соединения; параллельные вызовы разделяют одно ожидание. */
  private openConnection(socket: Socket): Promise<Socket> {
    if (this.pendingConnection) {
      return this.pendingConnection.promise;
    }

    let resolveConnection!: (value: Socket) => void;
    let rejectConnection!: (reason: Error) => void;
    const promise = new Promise<Socket>((resolve, reject) => {
      resolveConnection = resolve;
      rejectConnection = reject;
    });

    const settle = () => {
      this.pendingConnection = null;
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleError);
    };

    const handleConnect = () => {
      settle();
      resolveConnection(socket);
    };

    const handleError = (error: Error) => {
      settle();
      // Сокет, за который никто не держится, закрываем: иначе следующий connect() будет ждать мёртвый инстанс
      if (this.refCount <= 0) {
        this.closeSocket();
      }
      rejectConnection(error);
    };

    this.pendingConnection = {
      promise,
      abort: (reason) => {
        settle();
        rejectConnection(reason);
      },
    };

    socket.on('connect', handleConnect);
    socket.on('connect_error', handleError);
    socket.connect();

    return promise;
  }

  /** Закрывает сокет и сбрасывает состояние соединения к исходному. */
  private closeSocket(): void {
    this.pendingConnection?.abort(new Error('AI socket connection was closed before it opened'));
    this.socket?.close();
    this.socket = null;
    this.refCount = 0;
  }
}
